/**
 * RSAPanel Component
 * Displays Responsive Search Ads in an ad group with edit capability
 */

import React, { useCallback } from 'react';
import type { AdGroup, Ad } from '@/types/campaign';
import { getRSAsFromAdGroup } from '../utils';

interface RSAPanelProps {
  adGroup: AdGroup;
  onEditRSA: (adId: string) => void;
}

export function RSAPanel({ adGroup, onEditRSA }: RSAPanelProps) {
  const rsas = getRSAsFromAdGroup(adGroup);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (adId: string) => (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onEditRSA(adId);
      }
    },
    [onEditRSA]
  );

  // Get display text for an RSA
  const getDisplayText = (rsa: Ad): string => {
    const headlines = rsa.headlines || [];
    const headlineTexts = headlines.map(h =>
      typeof h === 'string' ? h : h.text
    );

    if (headlineTexts.length > 0) {
      return headlineTexts.slice(0, 3).join(' | ');
    }

    return rsa.ad_name || `RSA ${rsa.ad_id.slice(-6)}`;
  };

  if (rsas.length === 0) {
    return (
      <div className="p-4">
        <div className="text-center py-6 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-600">No Responsive Search Ads in this ad group</p>
          <p className="text-sm text-gray-500 mt-1">
            Create RSAs in Google Ads to manage them here
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <p className="text-xs text-gray-500 mb-3">
        Click on a Responsive Search Ad to edit its headlines and descriptions
      </p>

      <div className="space-y-2">
        {rsas.map((rsa) => (
          <div
            key={rsa.ad_id}
            role="button"
            tabIndex={0}
            className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            onClick={() => onEditRSA(rsa.ad_id)}
            onKeyDown={handleKeyDown(rsa.ad_id)}
            aria-label={`Edit RSA: ${getDisplayText(rsa)}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-1 min-w-0 mr-2">
                <span className={`text-xs font-medium flex-shrink-0 ${
                  rsa.status === 'PAUSED' ? 'text-yellow-600' : 'text-blue-600'
                }`}>
                  RSA{rsa.status === 'PAUSED' ? ' (Paused)' : ''}:
                </span>
                <p className="text-sm font-medium text-gray-900 truncate">
                  {getDisplayText(rsa)}
                </p>
              </div>
              <span
                className="text-xs text-blue-600 hover:text-blue-800 flex-shrink-0"
                aria-hidden="true"
              >
                Edit
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default RSAPanel;
